import React, { useState, useEffect, useRef } from 'react'
import { Icon } from '../Icon'

interface Option {
  iconName?: string
  value: string
  label: string
}

interface Props {
  options: Option[]
  title: string
  placeholder?: string
  error?: string | undefined
  iconName?: string
  labelClass?: string
  selectedOptionClass?: string
  value?: string
  onChange?: (selectedValue: string) => void // New onChange prop
  onBlur?: () => void // New onChange prop
  disabled?:boolean
}

const UiSelect: React.FC<Props> = ({
  options,
  title,
  placeholder,
  error,
  iconName,
  labelClass,
  selectedOptionClass,
  value,
  onChange, // New onChange prop
  onBlur,
  disabled
}) => {
  const [selectedOption, setSelectedOption] = useState<Option | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null) // Ref for the wrapper div

  const [isOpen, setIsOpen] = useState<boolean>(false)

  useEffect(() => {
    if (value) {
      const foundOption = options.find((option) => option.value === value)
      if (foundOption) {
        setSelectedOption(foundOption)
      }
    }
  }, [value, options])

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      // Check if the click is outside the component
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false) // Close the dropdown if it's open
        if (onBlur) {
          onBlur() // Call the onBlur function if provided
        }
      }
    }
    // Add event listener for clicks outside the component
    document.addEventListener('mousedown', handleOutsideClick)
    // Clean up the event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [onBlur])

  const toggleSelect = () => {
    setIsOpen(!isOpen)
  }

  const handleOptionClick = (option: Option) => {
    setSelectedOption(option)
    setIsOpen(false)
    // Call the onChange function with the selected option's value
    if (onChange) {
      onChange(option.value)
    }
  }

  return (
    <div ref={wrapperRef} className={`relative w-[100%] ${disabled ? ' pointer-events-none' : ''}`}>
      <label className="text-xs font-medium text-text1">{title}</label>
      <div
        className={`w-[100%] mt-[10px] h-auto p-3 border rounded-xl flex  gap-2 items-center cursor-pointer ${disabled ? 'bg-gray2' : ''} ${
          error ? 'border-textError' : 'border-gray3'
        }`}
        onClick={toggleSelect}
      >
        {iconName && (
          <Icon
            name={iconName}
            width={16}
            height={16}
            strokeWidth={2.67}
            stroke={error ? '#FF6666' : '#02698C'}
          />
        )}
        <span
          className={`text-[14px] font-semibold flex-1 ${
            error
              ? 'text-textError'
              : selectedOption
                ? 'text-text4'
                : 'text-text3'
          } ${selectedOptionClass}`}
        >
          {selectedOption
            ? selectedOption.label
            : placeholder
              ? placeholder
              : 'Select an option'}
        </span>
        <Icon
          name={'IconArrowDown'}
          width={16}
          height={16}
          strokeWidth={1}
          stroke="#02698C"
        />
      </div>
      {isOpen && (
        <div
          style={{ scrollbarWidth: 'none' }}
          className="absolute mt-1 w-full bg-white border max-h-[300px] overflow-hidden overflow-y-scroll overflow-x-hidden border-gray3 rounded-md shadow-lg z-10"
        >
          {options?.map((option, index) => (
            <div
              key={index}
              className="p-2 flex items-center gap-2 cursor-pointer hover:bg-gray1 m-1 hover:rounded-sm group"
              onClick={() => handleOptionClick(option)}
            >
              {option?.iconName && (
                <Icon
                  name={option?.iconName}
                  width={16}
                  height={16}
                  strokeWidth={1}
                  stroke={'#02698C'}
                />
              )}
              <p
                className={`font-semibold text-[14px] text-text3 group-hover:text-text2  ${labelClass}`}
              >
                {option.label}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default UiSelect
